import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Leaf, Quote, ShieldCheck, User } from 'lucide-react';
import { BRAND_PHILOSOPHY } from '../../data/brandData';

const iconMap: Record<string, React.ReactNode> = {
  ShieldCheck: <ShieldCheck className="w-6 h-6" />,
  Cpu: <Cpu className="w-6 h-6" />,
  Leaf: <Leaf className="w-6 h-6" />,
  User: <User className="w-6 h-6" />
};

const cardShells = [
  'bg-white',
  'bg-[#f4ede2]',
  'bg-[#eef3ed]',
  'bg-[#f8f5ee]'
];

export function BrandPhilosophy() {
  const { description, values, ceoQuote } = BRAND_PHILOSOPHY;

  return (
    <section className="py-24 bg-[var(--zw-ivory)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-[8%] h-52 w-52 rounded-full bg-[#ebdcc7]/50 blur-3xl" />
        <div className="absolute bottom-8 right-[6%] h-64 w-64 rounded-full bg-[#e0eadc]/60 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch mb-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.65 }}
            className="rounded-[2.2rem] bg-white border border-black/5 p-8 md:p-10 lg:p-12 shadow-[0_20px_70px_rgba(0,0,0,0.05)]"
          >
            <span className="home-kicker">Nuestra filosofia</span>
            <h2 className="home-title mb-6">
              La movilidad no deberia sentirse generica
            </h2>
            <p className="home-copy max-w-2xl">
              {description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="relative overflow-hidden rounded-[2.2rem] bg-[linear-gradient(135deg,#111111_0%,#1a1a1a_58%,#18392b_100%)] text-white p-8 md:p-10 lg:p-12 shadow-[0_28px_90px_rgba(0,0,0,0.16)]"
          >
            <div className="absolute top-8 right-8 opacity-10">
              <Quote className="w-24 h-24" />
            </div>

            <div className="relative z-10 flex flex-col justify-between h-full">
              <div>
                <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-6">
                  Vision Zwheel
                </span>
                <blockquote className="text-2xl md:text-3xl font-semibold leading-[1.35] tracking-tight text-white mb-6">
                  “{ceoQuote}”
                </blockquote>
              </div>

              <div>
                <div className="w-14 h-[2px] bg-white/20 rounded-full mb-4" />
                <div className="text-sm uppercase tracking-[0.18em] text-white/55">Equipo directivo</div>
                <div className="text-lg font-semibold text-white mt-1">Zwheel</div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.article
              key={value.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: index * 0.08 }}
              className={`group rounded-[1.85rem] border border-black/5 p-7 shadow-[0_18px_60px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 ${cardShells[index % cardShells.length]}`}
            >
              <div className="inline-flex items-center justify-center rounded-2xl bg-black text-white p-4 mb-6 shadow-lg transition-transform duration-300 group-hover:scale-105">
                {iconMap[value.icon]}
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
                {value.subtitle}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
              <p className="text-gray-600 leading-relaxed">{value.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
